import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrentUserPersona } from '../../hooks/useCurrentUserPersona';
import { PatientFamilyView } from '../../components/family/PatientFamilyView';
import { AgentFamilyView } from '../../components/family/AgentFamilyView';

/**
 * ECN Subtask 9 (CU-86ahjxrgg) — the persona-polymorphic "My MIDEO Family"
 * surface (Persona & Sessions-Surface Convergence redesign §3.1).
 *
 * One root-level route (outside navObj, like /sessions) serving BOTH personas:
 *   - Patient (isPatient): manage the agents they designated (HCA + ≤2 alternates)
 *     — invite / remove + inline consent assent.
 *   - Agent-only (isMIDEOFamilyMember && !isPatient): view the patients they are an
 *     accepted agent of, edit their OWN contact details, and step down.
 *
 * This is the ONE non-/sessions surface an agent-only user can reach (§3.1
 * amendment to the original "agent shell = /sessions only"). Both personas reach
 * it via a static menu item (Header + AgentHeader), NOT navObj.
 */
export const Route = createFileRoute('/_authenticated/myMideoFamily')({
  component: MyMideoFamily,
});

function MyMideoFamily() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { isPatient, isMIDEOFamilyMember, personaResolved } = useCurrentUserPersona();

  // Independent unauth gate (defense-in-depth), mirroring /sessions: this surface
  // is reachable by agent-only users whose chrome has no patient guards, and
  // Firebase auth persistence can linger across a reload after an interrupted
  // sign-out. `undefined` = auth initializing (wait); `null` = signed out.
  useEffect(() => {
    if (currentUser === null) {
      window.localStorage.setItem('requestedURL', window.location.href);
      navigate({ to: '/login', replace: true });
    }
  }, [currentUser, navigate]);

  if (!currentUser) {
    return null;
  }

  // Wait for the persona to resolve before choosing a view, so we never flash the
  // wrong one during the first-login PatientDoc race.
  if (!personaResolved) {
    return (
      <div className="flex w-full max-w-3xl flex-col items-center gap-4 text-center">
        <p className="text-gray-600">Loading…</p>
      </div>
    );
  }

  // Patient (incl. both-persona users) manage their designated agents.
  if (isPatient) {
    return <PatientFamilyView />;
  }

  // Agent-only: their self-service surface.
  if (isMIDEOFamilyMember) {
    return <AgentFamilyView agentUid={currentUser.uid} />;
  }

  // Neither persona (e.g. a brand-new doc-less user). Non-crashing empty state;
  // the "Become a patient" CTA is post-MVP (redesign §3.1).
  return (
    <div className="flex w-full max-w-3xl flex-col items-center gap-4 text-center">
      <h1 className="text-2xl font-semibold text-sky-800">My MIDEO Family</h1>
      <p className="text-gray-600">There is nothing to manage here yet.</p>
    </div>
  );
}

export default MyMideoFamily;
